import { useState, useEffect, useRef } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import styles from "./AddAirlineModal.module.css";

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "East Timor", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia",
  "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada",
  "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hong Kong", "Hungary", "Iceland",
  "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan",
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
  "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macedonia", "Madagascar",
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
  "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique",
  "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
  "North Korea", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
  "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
  "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan",
  "Spain", "Sri Lanka", "Sudan", "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "Taiwan",
  "Tajikistan", "Tanzania", "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const CHECKIN_STATUSES = [
  { value: "", label: "Not configured (use system default)" },
  { value: "required", label: "Required" },
  { value: "recommended", label: "Recommended" },
  { value: "not_required", label: "Not required" },
];
const CHECKIN_REQUIRED_FIELDS = ["Ticket No", "PNR", "Last Name"];

const emptyForm = {
  airlineName: "",
  iataCode: "",
  icaoCode: "",
  country: "",
  status: "active",
  logo: null,
  logoPreview: null,
  checkinStatus: "",
  checkinRequiredFields: [],
  checkinText: "",
  checkinUrl: "",
};

const AddAirlineModal = ({ open, onClose, onSave, airline }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (airline) {
      setForm({
        airlineName: airline.airlineName || "",
        iataCode: airline.iataCode || "",
        icaoCode: airline.icaoCode || "",
        country: airline.country || "",
        status: airline.status || "active",
        logo: airline.logo || null,
        logoPreview: airline.logoUrl || null,
        checkinStatus: airline.checkinStatus || "",
        checkinRequiredFields: airline.checkinRequiredFields || [],
        checkinText: airline.checkinText || "",
        checkinUrl: airline.checkinUrl || "",
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
    setApiError(null);
  }, [airline, open]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "airlineName":
        if (!value || !value.trim()) {
          error = "Airline name is required";
        } else if (value.trim().length < 2) {
          error = "Airline name must be at least 2 characters";
        }
        break;
      case "iataCode":
        if (!value || !value.trim()) {
          error = "IATA code is required";
        } else if (!/^[A-Z0-9]{2}$/.test(value)) {
          error = "IATA code must be exactly 2 uppercase letters";
        }
        break;
      case "icaoCode":
        if (!/^[A-Z0-9]{3}$/.test(value)) {
          error = "ICAO code must be exactly 3 uppercase letters";
        }
        break;
      case "country":
        if (!value) {
          error = "Country is required";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!form.airlineName.trim()) {
      newErrors.airlineName = "Airline name is required";
      isValid = false;
    } else if (form.airlineName.trim().length < 2) {
      newErrors.airlineName = "Airline name must be at least 2 characters";
      isValid = false;
    }

    if (!form.iataCode.trim()) {
      newErrors.iataCode = "IATA code is required";
      isValid = false;
    } else if (!/^[A-Z0-9]{2}$/.test(form.iataCode)) {
      newErrors.iataCode = "IATA code must be exactly 2 uppercase letters";
      isValid = false;
    }

    if (form.icaoCode && !/^[A-Z0-9]{3}$/.test(form.icaoCode)) {
      newErrors.icaoCode = "ICAO code must be exactly 3 uppercase letters";
      isValid = false;
    }

    if (!form.country) {
      newErrors.country = "Country is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // --- Logo Handlers ---

  const processLogoFile = (file) => {
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo: "Logo must be a PNG, JPG, SVG, or WebP image" }));
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, logo: "Logo file size must be under 2MB" }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setForm(prev => ({ ...prev, logo: file, logoPreview: e.target.result }));
      setErrors(prev => ({ ...prev, logo: "" }));
    };
    reader.readAsDataURL(file);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    processLogoFile(file);
    // Reset input so the same file can be re-selected after removal
    e.target.value = "";
  };

  const handleLogoDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleLogoDragLeave = () => {
    setIsDragging(false);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    processLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setForm(prev => ({ ...prev, logo: null, logoPreview: null }));
    setErrors(prev => ({ ...prev, logo: "" }));
  };

  // --- Reset / Close / Submit ---

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();
    if (!isValid) return;

    setApiError(null);
    setSaving(true);

    const payload = {
      airlineName: form.airlineName.trim(),
      iataCode: form.iataCode.trim().toUpperCase(),
      icaoCode: form.icaoCode.trim().toUpperCase() || null,
      country: form.country,
      status: form.status,
      logo: form.logo || null,
      checkinStatus: form.checkinStatus || null,
      checkinRequiredFields: form.checkinStatus ? form.checkinRequiredFields : [],
      checkinText: form.checkinStatus ? form.checkinText.trim() : "",
      checkinUrl: form.checkinStatus ? form.checkinUrl.trim() : "",
    };

    try {
      const errorMsg = await onSave(payload);
      if (errorMsg) {
        setApiError(errorMsg);
      } else {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={airline ? "Edit Airline" : "Add New Airline"}
      width="680px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </>
      }
    >
      {/* Icon Header */}
      <div className={styles.iconHeader}>
        <div className={styles.icon}>✈️</div>
        <div className={styles.headerText}>
          <h3>{airline ? "Edit Airline Details" : "Add New Airline"}</h3>
          <p>
            {airline
              ? "Update airline master data used across bookings and operations"
              : "Create a new airline entry for flight bookings and operations"}
          </p>
        </div>
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Save failed:</strong> {apiError}
          </div>
        </div>
      )}

      {/* Validation Error Banner */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) =>
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>✏️ BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>AIRLINE NAME <span className={styles.required}>*</span></label>
            <input
              value={form.airlineName}
              onChange={e => handleChange("airlineName", e.target.value)}
              onBlur={() => handleBlur("airlineName")}
              placeholder="e.g., Emirates, Qatar Airways, Lufthansa"
              className={errors.airlineName ? styles.inputError : ""}
            />
            {errors.airlineName && (
              <span className={styles.errorText}>{errors.airlineName}</span>
            )}
            <span className={styles.helpText}>
              Full legal name of the airline as it appears in official documents
            </span>
          </div>

          <div className={styles.field}>
            <label>IATA CODE <span className={styles.required}>*</span></label>
            <input
              value={form.iataCode}
              onChange={e => handleChange("iataCode", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("iataCode")}
              placeholder="EK"
              maxLength={2}
              className={`${styles.codeInput} ${errors.iataCode ? styles.inputError : ""}`}
            />
            {errors.iataCode && (
              <span className={styles.errorText}>{errors.iataCode}</span>
            )}
            <span className={styles.helpText}>
              2-letter IATA airline code (required)
            </span>
          </div>

          <div className={styles.field}>
            <label>ICAO CODE</label>
            <input
              value={form.icaoCode}
              onChange={e => handleChange("icaoCode", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("icaoCode")}
              placeholder="UAE"
              maxLength={3}
              className={`${styles.codeInput} ${errors.icaoCode ? styles.inputError : ""}`}
            />
            {errors.icaoCode && (
              <span className={styles.errorText}>{errors.icaoCode}</span>
            )}
            <span className={styles.helpText}>
              3-letter ICAO airline code (optional)
            </span>
          </div>

          <div className={styles.field}>
            <label>COUNTRY OF ORIGIN <span className={styles.required}>*</span></label>
            <select
              value={form.country}
              onChange={e => handleChange("country", e.target.value)}
              onBlur={() => handleBlur("country")}
              className={errors.country ? styles.inputError : ""}
            >
              <option value="">Select country</option>
              {COUNTRIES.map(country => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            {errors.country && (
              <span className={styles.errorText}>{errors.country}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <div className={styles.statusToggle}>
              <input
                type="checkbox"
                id="airlineStatus"
                checked={form.status === "active"}
                onChange={e => handleChange("status", e.target.checked ? "active" : "inactive")}
                className={styles.toggleInput}
              />
              <label htmlFor="airlineStatus" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch}></span>
                <span className={styles.toggleText}>
                  {form.status === "active" ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
            <span className={styles.helpText}>
              Only active airlines appear in flight search and bookings
            </span>
          </div>
        </div>
      </div>

 <div className={styles.section}>
        <h4 className={styles.sectionTitle}>🖼️ AIRLINE LOGO</h4>

        {form.logoPreview ? (
          <div className={styles.logoPreviewWrapper}>
            <div className={styles.logoPreviewBox}>
              <img
                src={form.logoPreview}
                alt="Airline logo preview"
                className={styles.logoPreviewImg}
              />
            </div>
            <div className={styles.logoPreviewMeta}>
              <p className={styles.logoFileName}>
                {form.logo?.name || "Current logo"}
              </p>
              {form.logo?.size && (
                <p className={styles.logoFileSize}>
                  {(form.logo.size / 1024).toFixed(1)} KB
                </p>
              )}
              <div className={styles.logoActions}>
                <button
                  type="button"
                  className={styles.logoChangeBtn}
                  onClick={() => fileInputRef.current?.click()}
                >
                  🔄 Change Logo
                </button>
                <button
                  type="button"
                  className={styles.logoRemoveBtn}
                  onClick={handleRemoveLogo}
                >
                  🗑️ Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`${styles.logoDropZone} ${isDragging ? styles.logoDropZoneActive : ""} ${errors.logo ? styles.logoDropZoneError : ""}`}
            onDragOver={handleLogoDragOver}
            onDragLeave={handleLogoDragLeave}
            onDrop={handleLogoDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={styles.logoDropIcon}>🏷️</div>
            <p className={styles.logoDropTitle}>
              {isDragging ? "Drop your logo here" : "Drag & drop airline logo"}
            </p>
            <p className={styles.logoDropSub}>or click to browse files</p>
            <p className={styles.logoDropHint}>PNG, JPG, SVG, WebP · Max 2MB</p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          className={styles.logoFileInput}
          onChange={handleLogoFileChange}
        />

        {errors.logo && (
          <span className={styles.errorText}>{errors.logo}</span>
        )}
        <span className={styles.helpText}>
          Logo appears in flight listings, booking confirmations, and airline search results
        </span>
      </div>
      {/* Online Check-in Default Configuration */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>ONLINE CHECK-IN DEFAULT</h4>
        <p style={{ fontSize: "12px", color: "#666", marginBottom: "12px" }}>
          Airline-level default used when a fare family has no check-in config set.
        </p>
        <div className={styles.formGrid}>
          <div className={styles.fieldWide}>
            <label>CHECK-IN STATUS</label>
            <select
              value={form.checkinStatus}
              onChange={e => handleChange("checkinStatus", e.target.value)}
            >
              {CHECKIN_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className={styles.helpText}>
              This is the fallback for all fare families of this airline that don't have their own config.
            </span>
          </div>

          {form.checkinStatus && (
            <>
              <div className={styles.fieldWide}>
                <label>REQUIRED FIELDS FOR CHECK-IN</label>
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  {CHECKIN_REQUIRED_FIELDS.map((field) => (
                    <label key={field} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                      <input
                        type="checkbox"
                        checked={form.checkinRequiredFields.includes(field)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...form.checkinRequiredFields, field]
                            : form.checkinRequiredFields.filter((f) => f !== field);
                          handleChange("checkinRequiredFields", updated);
                        }}
                      />
                      <span>{field}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.fieldWide}>
                <label>CUSTOM INSTRUCTION TEXT</label>
                <input
                  value={form.checkinText}
                  onChange={e => handleChange("checkinText", e.target.value)}
                  placeholder="Leave empty to auto-generate based on status"
                />
                <span className={styles.helpText}>Optional custom text shown on flight vouchers</span>
              </div>

              <div className={styles.fieldWide}>
                <label>CHECK-IN URL</label>
                <input
                  value={form.checkinUrl}
                  onChange={e => handleChange("checkinUrl", e.target.value)}
                  placeholder="e.g., https://www.airline.com/checkin"
                />
                <span className={styles.helpText}>Airline's online check-in page URL</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Airline data is synced via API and used across the system</li>
            <li>IATA codes must be unique across all airlines</li>
            <li>Inactive airlines won't appear in flight search results</li>
            <li>Check-in config here is the fallback for fare families without their own config</li>
            <li>Priority: Fare-level → Airline-level → System default</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddAirlineModal;